import { useEffect, useState } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { NAV_LINKS, SITE } from "@/lib/site";
import { Menu, X, ArrowUpRight } from "lucide-react";

export default function Navbar() {
  const [scrolled, setScrolled] = useState(false);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 24);
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  return (
    <motion.header
      data-testid="site-navbar"
      initial={{ y: -20, opacity: 0 }}
      animate={{ y: 0, opacity: 1 }}
      transition={{ duration: 0.7, delay: 0.15, ease: [0.22, 1, 0.36, 1] }}
      className="fixed top-4 left-0 right-0 z-40 px-4 md:px-8"
    >
      <div
        className={`max-w-[1160px] mx-auto flex items-center justify-between gap-4 pl-5 pr-2 h-14 md:h-16 rounded-full transition-all duration-300 ${
          scrolled
            ? "bg-white/85 backdrop-blur-xl border border-black/[0.06] shadow-[0_10px_40px_-12px_rgba(11,11,20,0.15)]"
            : "bg-[rgba(255,255,255,0.35)] backdrop-blur-xl border border-white/40"
        }`}
      >
        <a href="#top" data-testid="brand-logo" className="flex items-center gap-2 group">
          <img
            src={SITE.logoUrl}
            alt="Clamignon"
            className="h-10 md:h-12 w-auto object-contain"
          />
        </a>

        <ul className="hidden md:flex items-center gap-1">
          {NAV_LINKS.map((l) => (
            <li key={l.id}>
              <a
                data-testid={`nav-link-${l.id}`}
                href={`#${l.id}`}
                className="inline-flex items-center px-4 py-2 rounded-full text-[13.5px] font-semibold text-[#1E1E2C] hover:bg-white/70 transition-colors"
              >
                {l.label}
              </a>
            </li>
          ))}
        </ul>

        <div className="flex items-center gap-2">
          <a
            data-testid="nav-cta-button"
            href="#contacto"
            className="btn-pill btn-primary shine text-sm px-5 py-2.5 hidden sm:inline-flex"
          >
            Escríbenos!
          </a>
          <button
            data-testid="mobile-menu-toggle"
            aria-label="Menú"
            onClick={() => setOpen((v) => !v)}
            className="md:hidden w-10 h-10 rounded-full bg-white/70 border border-black/10 flex items-center justify-center text-[#0B0B14]"
          >
            {open ? <X className="w-4 h-4" /> : <Menu className="w-4 h-4" />}
          </button>
        </div>
      </div>

      <AnimatePresence>
        {open && (
          <motion.div
            initial={{ opacity: 0, y: -8 }}
            animate={{ opacity: 1, y: 0 }}
            exit={{ opacity: 0, y: -8 }}
            transition={{ duration: 0.3 }}
            className="md:hidden mt-2 max-w-[1160px] mx-auto rounded-3xl bg-white/95 backdrop-blur-xl border border-black/[0.06] shadow-xl overflow-hidden"
          >
            <ul className="p-4 space-y-1">
              {NAV_LINKS.map((l) => (
                <li key={l.id}>
                  <a
                    data-testid={`mobile-nav-${l.id}`}
                    href={`#${l.id}`}
                    onClick={() => setOpen(false)}
                    className="flex items-center justify-between px-4 py-3 rounded-2xl text-[#0B0B14] hover:bg-[#F5F3FA]"
                  >
                    <span className="font-semibold">{l.label}</span>
                    <ArrowUpRight className="w-4 h-4 text-[#7C6AF5]" />
                  </a>
                </li>
              ))}
              <li className="pt-2">
                <a
                  data-testid="mobile-cta"
                  href="#contacto"
                  onClick={() => setOpen(false)}
                  className="btn-pill btn-primary w-full"
                >
                  Escríbenos!
                </a>
              </li>
            </ul>
          </motion.div>
        )}
      </AnimatePresence>
    </motion.header>
  );
}
